import { memo, useState, useEffect } from 'react';
import styles from './SymbolSearch.module.css';

const SymbolSearch = ({
  containerRef,
  symbol,
  suggestions,
  showSuggestions,
  onSymbolChange,
  onSuggestionSelect,
  onFocus,
  onClose,
  onSubmit,
  status,
}) => {
  const [activeIndex, setActiveIndex] = useState(-1);

  // Reset activeIndex when suggestions change or list closes
  useEffect(() => {
    setActiveIndex(-1);
  }, [suggestions, showSuggestions]);

  const isExpanded = showSuggestions && suggestions.length > 0;

  const handleKeyDown = (event) => {
    if (!isExpanded) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        event.preventDefault();
        if (activeIndex >= 0 && activeIndex < suggestions.length) {
          onSuggestionSelect(suggestions[activeIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
      default:
        break;
    }
  };

  return (
    <div className={styles.inputField} ref={containerRef}>
      <span className={styles.fieldLabel}>Company / ticker</span>
      <div className={styles.searchRow}>
        <input
          type="text"
          value={symbol}
          onChange={onSymbolChange}
          placeholder={symbol}
          onFocus={onFocus}
          onKeyDown={handleKeyDown}
          aria-label="Search stock symbol"
          role="combobox"
          aria-expanded={isExpanded ? 'true' : 'false'}
          aria-controls={isExpanded ? 'symbol-suggestions' : undefined}
          aria-activedescendant={
            isExpanded && activeIndex >= 0
              ? `symbol-option-${activeIndex}`
              : undefined
          }
        />

        {onSubmit && (
          <button
            type="button"
            className={styles.goButton}
            onClick={onSubmit}
            disabled={status === 'fetching'}
            aria-busy={status === 'fetching' ? 'true' : 'false'}
            {...(status === 'fetching' ? { 'aria-disabled': 'true' } : {})}
          >
            Go
          </button>
        )}
      </div>

      {isExpanded && (
        <div
          id="symbol-suggestions"
          className={styles.suggestionsContainer}
          role="listbox"
        >
          {suggestions.map((item, index) => (
            <div
              key={index}
              id={`symbol-option-${index}`}
              className={`${styles.suggestionItem}${
                index === activeIndex ? ` ${styles.suggestionItemActive}` : ''
              }`}
              role="option"
              aria-selected={index === activeIndex}
              onClick={() => onSuggestionSelect(item)}
            >
              <span className={styles.exchangeSymbol}>
                <span className={styles.exchange}>{item.exchange}:</span>
                <span className={styles.symbol}>{item.symbol}</span>
              </span>
              <span className={styles.name}>{item.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(SymbolSearch);
